import { useEffect } from "react";
import { defaultImage, getCanonicalUrl, siteName } from "../seo/seoConfig";

function upsertMeta(selector, createElement, updateElement) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = createElement();
    document.head.appendChild(element);
  }

  updateElement(element);
}

export default function Seo({ config, pathname }) {
  useEffect(() => {
    const title = config.title || siteName;
    const description = config.description || "";
    const canonicalUrl = getCanonicalUrl(pathname);
    const imageUrl = config.image || defaultImage;
    const robotsContent = config.noindex ? "noindex, nofollow, noarchive" : "index, follow, max-image-preview:large";

    document.title = title;

    upsertMeta(
      'meta[name="description"]',
      () => {
        const meta = document.createElement("meta");
        meta.setAttribute("name", "description");
        return meta;
      },
      (meta) => meta.setAttribute("content", description)
    );

    upsertMeta(
      'meta[name="robots"]',
      () => {
        const meta = document.createElement("meta");
        meta.setAttribute("name", "robots");
        return meta;
      },
      (meta) => meta.setAttribute("content", robotsContent)
    );

    upsertMeta(
      'meta[name="keywords"]',
      () => {
        const meta = document.createElement("meta");
        meta.setAttribute("name", "keywords");
        return meta;
      },
      (meta) => meta.setAttribute("content", config.keywords || "")
    );

    upsertMeta(
      'meta[property="og:type"]',
      () => {
        const meta = document.createElement("meta");
        meta.setAttribute("property", "og:type");
        return meta;
      },
      (meta) => meta.setAttribute("content", config.ogType || "website")
    );

    upsertMeta(
      'meta[property="og:title"]',
      () => {
        const meta = document.createElement("meta");
        meta.setAttribute("property", "og:title");
        return meta;
      },
      (meta) => meta.setAttribute("content", title)
    );

    upsertMeta(
      'meta[property="og:description"]',
      () => {
        const meta = document.createElement("meta");
        meta.setAttribute("property", "og:description");
        return meta;
      },
      (meta) => meta.setAttribute("content", description)
    );

    upsertMeta(
      'meta[property="og:url"]',
      () => {
        const meta = document.createElement("meta");
        meta.setAttribute("property", "og:url");
        return meta;
      },
      (meta) => meta.setAttribute("content", canonicalUrl)
    );

    upsertMeta(
      'meta[property="og:image"]',
      () => {
        const meta = document.createElement("meta");
        meta.setAttribute("property", "og:image");
        return meta;
      },
      (meta) => meta.setAttribute("content", imageUrl)
    );

    upsertMeta(
      'meta[property="og:image:width"]',
      () => {
        const meta = document.createElement("meta");
        meta.setAttribute("property", "og:image:width");
        return meta;
      },
      (meta) => meta.setAttribute("content", "512")
    );

    upsertMeta(
      'meta[property="og:image:height"]',
      () => {
        const meta = document.createElement("meta");
        meta.setAttribute("property", "og:image:height");
        return meta;
      },
      (meta) => meta.setAttribute("content", "512")
    );

    upsertMeta(
      'meta[property="og:site_name"]',
      () => {
        const meta = document.createElement("meta");
        meta.setAttribute("property", "og:site_name");
        return meta;
      },
      (meta) => meta.setAttribute("content", siteName)
    );

    upsertMeta(
      'meta[name="twitter:card"]',
      () => {
        const meta = document.createElement("meta");
        meta.setAttribute("name", "twitter:card");
        return meta;
      },
      (meta) => meta.setAttribute("content", "summary_large_image")
    );

    upsertMeta(
      'meta[name="twitter:title"]',
      () => {
        const meta = document.createElement("meta");
        meta.setAttribute("name", "twitter:title");
        return meta;
      },
      (meta) => meta.setAttribute("content", title)
    );

    upsertMeta(
      'meta[name="twitter:description"]',
      () => {
        const meta = document.createElement("meta");
        meta.setAttribute("name", "twitter:description");
        return meta;
      },
      (meta) => meta.setAttribute("content", description)
    );

    upsertMeta(
      'meta[name="twitter:image"]',
      () => {
        const meta = document.createElement("meta");
        meta.setAttribute("name", "twitter:image");
        return meta;
      },
      (meta) => meta.setAttribute("content", imageUrl)
    );

    upsertMeta(
      'link[rel="canonical"]',
      () => {
        const link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        return link;
      },
      (link) => link.setAttribute("href", canonicalUrl)
    );

    document.querySelectorAll('script[data-seo-schema="true"]').forEach((script) => {
      script.remove();
    });

    (config.schema || []).forEach((schema, index) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.seoSchema = "true";
      script.dataset.schemaIndex = String(index);
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, [config, pathname]);

  return null;
}
